import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Tab, Tabs,
  TextField, CircularProgress, Divider, Tooltip, Alert, List,
  ListItem, ListItemText, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Switch, FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PollIcon from '@mui/icons-material/Poll';
import DownloadingIcon from '@mui/icons-material/Downloading';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  doc, getDoc, collection, addDoc, updateDoc, onSnapshot,
  serverTimestamp, getDocs, query, orderBy, runTransaction,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { WorkshopNLogo } from './WorkshopNAdmin';

const accent = '#E05500';

function ResponseRing({ answered, total, size = 48, strokeWidth = 4 }) {
  const pct = total > 0 ? answered / total : 0;
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const color = pct === 1 ? '#4ade80' : pct >= 0.5 ? accent : '#94a3b8';
  return (
    <Box component="svg" width={size} height={size} sx={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="currentColor" strokeWidth={strokeWidth} style={{ color: 'rgba(0,0,0,0.08)' }} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        style={{ fontSize: size * 0.24, fontWeight: 700, fill: color, fontFamily: 'inherit' }}>
        {total > 0 ? `${Math.round(pct * 100)}%` : '–'}
      </text>
    </Box>
  );
}

const GENERISKA_FRÅGOR_24H = [
  { id: 'g1', fråga: 'Hur upplevde du workshopen som arbetsform?', typ: 'skala', inkludera: true },
  { id: 'g2', fråga: 'Kände du att din röst kom fram?', typ: 'skala', inkludera: true },
  { id: 'g3', fråga: 'Hur väl stämmer resultatet med din bild av verkligheten?', typ: 'skala', inkludera: true },
];
const GENERISKA_FRÅGOR_1VECKA = [
  ...GENERISKA_FRÅGOR_24H,
  { id: 'g4', fråga: 'Har något av åtgärdsförslagen redan påbörjats?', typ: 'janej', inkludera: true },
];

const AI_BLOCKS = [
  { key: 'konklusion', label: 'Konklusion', get: ai => ai.konklusion || '' },
  { key: 'teman', label: 'Framträdande teman', get: ai => ai.teman?.length ? `Teman: ${ai.teman.join(', ')}` : '' },
  { key: 'styrkor', label: 'Styrkor', get: ai => ai.styrkör?.length ? `Styrkor:\n${ai.styrkör.map(s => `• ${s}`).join('\n')}` : '' },
  { key: 'utmaningar', label: 'Utmaningar', get: ai => ai.utmaningar?.length ? `Utmaningar:\n${ai.utmaningar.map(s => `• ${s}`).join('\n')}` : '' },
  { key: 'nastaSteg', label: 'Förslag nästa steg', get: ai => ai.forslagNastaSteg?.length ? `Nästa steg:\n${ai.forslagNastaSteg.map(s => `• ${s.steg}${s.prioritet === 'hög' ? ' (hög prio)' : ''}`).join('\n')}` : '' },
];

function buildSammanfattningFromAI(ai, selectedKeys) {
  return AI_BLOCKS
    .filter(b => selectedKeys.has(b.key))
    .map(b => b.get(ai))
    .filter(Boolean)
    .join('\n\n');
}

// Deltagarlistan (namn, e-post, token) ligger i enkäter/{id}/privat/deltagare
// som bara medarbetare kan läsa. Varje personlig länk har ett eget dokument,
// enkäter/{id}/lankar/{token}, med det EnkätSida behöver (namn, svarsstatus).
// Enkätdokumentet har bara antal: deltagareAntal och svaratAntal.
function deltagarRef(workshopId, enkätId) {
  return doc(db, 'workshops', workshopId, 'enkäter', enkätId, 'privat', 'deltagare');
}

// Ändrar deltagarlistan i en transaktion så att samtidiga svar (harSvarat,
// satt av Cloud Function onEnkatSvar) inte skrivs över.
async function uppdateraDeltagare(workshopId, enkätId, ändra) {
  const enkätRef = doc(db, 'workshops', workshopId, 'enkäter', enkätId);
  const ref = deltagarRef(workshopId, enkätId);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    const före = snap.exists() ? (snap.data().deltagare || []) : [];
    const efter = ändra(före);
    const kvar = new Set(efter.map(d => d.token));
    const fanns = new Set(före.map(d => d.token));
    for (const d of före) {
      if (!kvar.has(d.token)) tx.delete(doc(enkätRef, 'lankar', d.token));
    }
    for (const d of efter) {
      if (!fanns.has(d.token)) {
        tx.set(doc(enkätRef, 'lankar', d.token), { deltagarId: d.id, namn: d.namn, harSvarat: !!d.harSvarat });
      }
    }
    tx.set(ref, { deltagare: efter });
    tx.update(enkätRef, {
      deltagareAntal: efter.length,
      svaratAntal: efter.filter(d => d.harSvarat).length,
    });
  });
}

function genToken() {
  return Array.from({ length: 24 }, () => Math.random().toString(36)[2] || 'a').join('');
}

export default function WorkshopNEnkät() {
  const { id: workshopId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [workshop, setWorkshop] = useState(null);
  const [enkäter, setEnkäter] = useState([]);
  const [selectedEnkät, setSelectedEnkät] = useState(null);
  const [deltagare, setDeltagare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  // Deltagare
  const [addDialog, setAddDialog] = useState(false);
  const [addTab, setAddTab] = useState(0);
  const [newNamn, setNewNamn] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [workshopResponders, setWorkshopResponders] = useState([]);
  const [userFilterAO, setUserFilterAO] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedToAdd, setSelectedToAdd] = useState(new Set());
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Frågor
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState('');

  // Send
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Create
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTyp, setNewTyp] = useState('1vecka');

  // Svar (real-time)
  const [svar, setSvar] = useState([]);
  const [appfeedback, setAppfeedback] = useState([]);

  // Copied link
  const [copied, setCopied] = useState(null);

  // QR registrations
  const [qrRegistrations, setQrRegistrations] = useState([]);
  const [importingQR, setImportingQR] = useState(false);

  // AI summary import dialog
  const [aiImportDialog, setAiImportDialog] = useState(false);
  const [aiSelectedBlocks, setAiSelectedBlocks] = useState(new Set(AI_BLOCKS.map(b => b.key)));

  useEffect(() => {
    loadWorkshop();
    loadEnkäter();
    const regRef = query(collection(db, 'workshops', workshopId, 'registrations'), orderBy('registeredAt', 'desc'));
    const unsub = onSnapshot(regRef, snap => setQrRegistrations(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [workshopId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedEnkät when Firestore updates arrive
  useEffect(() => {
    if (!selectedEnkät || enkäter.length === 0) return;
    const updated = enkäter.find(e => e.id === selectedEnkät.id);
    if (updated) setSelectedEnkät(updated);
  }, [enkäter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEnkät) return;
    const enkätRef = doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id);
    setDeltagare([]);
    const unsubDeltagare = onSnapshot(deltagarRef(workshopId, selectedEnkät.id), snap => {
      setDeltagare(snap.exists() ? (snap.data().deltagare || []) : []);
    });
    const unsubSvar = onSnapshot(collection(enkätRef, 'svar'), snap => {
      // ogiltig/dubblett sätts av onEnkatSvar om token inte stämmer eller
      // deltagaren redan har svarat
      setSvar(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.ogiltig && !s.dubblett));
    });
    const unsubApp = onSnapshot(collection(enkätRef, 'appfeedback'), snap => {
      setAppfeedback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubDeltagare(); unsubSvar(); unsubApp(); };
  }, [selectedEnkät?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadWorkshop() {
    const snap = await getDoc(doc(db, 'workshops', workshopId));
    if (snap.exists()) setWorkshop({ id: snap.id, ...snap.data() });
  }

  function loadEnkäter() {
    const ref = collection(db, 'workshops', workshopId, 'enkäter');
    return onSnapshot(ref, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.skapadAt?.seconds || 0) - (a.skapadAt?.seconds || 0));
      setEnkäter(list);
      if (list.length > 0 && !selectedEnkät) setSelectedEnkät(list[0]);
      setLoading(false);
    });
  }

  async function handleCreateEnkät() {
    setCreating(true);
    const baseFrågor = newTyp === '1vecka' ? GENERISKA_FRÅGOR_1VECKA : GENERISKA_FRÅGOR_24H;
    const ref = await addDoc(collection(db, 'workshops', workshopId, 'enkäter'), {
      typ: newTyp,
      workshopTitle: workshop?.title || '',
      workshopDate: workshop?.date || '',
      workshopImageUrl: workshop?.imageUrl || '',
      skapadAv: currentUser.uid,
      skapadAt: serverTimestamp(),
      status: 'utkast',
      frågorInhåll: baseFrågor,
      deltagareAntal: 0,
      svaratAntal: 0,
      sammanfattning: '',
      nästaSteg: { aktiv: false, text: '', datum: '' },
    });
    const newE = { id: ref.id, typ: newTyp, workshopTitle: workshop?.title || '', workshopDate: workshop?.date || '', workshopImageUrl: workshop?.imageUrl || '', status: 'utkast', frågorInhåll: baseFrågor, deltagareAntal: 0, svaratAntal: 0, nästaSteg: { aktiv: false, text: '', datum: '' } };
    setSelectedEnkät(newE);
    setCreating(false);
    setCreateDialog(false);
    setTab(0);
  }

  async function openAddDialog() {
    setAddTab(0); setNewNamn(''); setNewEmail('');
    setSelectedToAdd(new Set()); setUserSearch(''); setUserFilterAO('');
    setLoadingPeople(true);
    setAddDialog(true);
    const [usersSnap, respSnap] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'workshops', workshopId, 'responses')),
    ]);
    const existing = new Set(deltagare.map(d => d.email));
    setAllUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.email && !existing.has(u.email)));
    const responders = [];
    for (const r of respSnap.docs) {
      const data = r.data();
      if (!data.userId) continue;
      const u = usersSnap.docs.find(d => d.id === data.userId)?.data();
      if (!u?.email || existing.has(u.email)) continue;
      if (!responders.find(x => x.email === u.email)) {
        responders.push({ id: data.userId, email: u.email, namn: data.displayName || u.displayName || u.email, departmentName: u.departmentName || '' });
      }
    }
    setWorkshopResponders(responders);
    setLoadingPeople(false);
  }

  async function handleAddDeltagare() {
    if (!newNamn.trim() || !newEmail.trim() || !selectedEnkät) return;
    const newD = { id: Date.now().toString(), namn: newNamn.trim(), email: newEmail.trim(), token: genToken(), harSvarat: false, skickatAt: null };
    await uppdateraDeltagare(workshopId, selectedEnkät.id, lista => [...lista, newD]);
    setNewNamn(''); setNewEmail('');
  }

  async function handleAddSelected() {
    if (selectedToAdd.size === 0) return;
    const source = addTab === 1 ? workshopResponders : allUsers;
    const existing = new Set(deltagare.map(d => d.email));
    const toAdd = source.filter(u => selectedToAdd.has(u.id || u.email) && !existing.has(u.email))
      .map(u => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 4),
        namn: u.namn || u.displayName || u.email,
        email: u.email,
        token: genToken(),
        harSvarat: false,
        skickatAt: null,
      }));
    if (toAdd.length === 0) { setAddDialog(false); return; }
    await uppdateraDeltagare(workshopId, selectedEnkät.id, lista => [...lista, ...toAdd]);
    setAddDialog(false);
  }

  async function handleImportQRRegistrations() {
    if (!selectedEnkät || qrRegistrations.length === 0) return;
    setImportingQR(true);
    const existing = new Set(deltagare.map(d => d.email.toLowerCase()));
    const toAdd = qrRegistrations
      .filter(r => r.email && !existing.has(r.email.toLowerCase()))
      .map(r => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 4),
        namn: r.namn,
        email: r.email,
        token: genToken(),
        harSvarat: false,
        skickatAt: null,
      }));
    if (toAdd.length > 0) {
      await uppdateraDeltagare(workshopId, selectedEnkät.id, lista => [...lista, ...toAdd]);
    }
    setImportingQR(false);
  }

  async function handleRemoveDeltagare(deltagarId) {
    await uppdateraDeltagare(workshopId, selectedEnkät.id, lista => lista.filter(d => d.id !== deltagarId));
  }

  async function handleGenerateQuestions() {
    setGeneratingQuestions(true);
    setQuestionsError('');
    try {
      const fn = httpsCallable(functions, 'genereraEnkatFragor');
      const result = await fn({ workshopId, enkätTyp: selectedEnkät.typ });
      const aiQuestions = (result.data.frågor || []).map((f, i) => ({
        id: `ai_${i}`, fråga: f, typ: 'text', inkludera: true,
      }));
      const baseFrågor = selectedEnkät.typ === '1vecka' ? GENERISKA_FRÅGOR_1VECKA : GENERISKA_FRÅGOR_24H;
      const updatedFrågor = [...baseFrågor, ...aiQuestions];
      await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { frågorInhåll: updatedFrågor });
      setSelectedEnkät(prev => ({ ...prev, frågorInhåll: updatedFrågor }));
    } catch (e) {
      setQuestionsError('Kunde inte generera frågor. Försök igen.');
    }
    setGeneratingQuestions(false);
  }

  async function toggleFråga(frågaId, inkludera) {
    const updatedFrågor = (selectedEnkät.frågorInhåll || []).map(f =>
      f.id === frågaId ? { ...f, inkludera } : f
    );
    await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { frågorInhåll: updatedFrågor });
    setSelectedEnkät(prev => ({ ...prev, frågorInhåll: updatedFrågor }));
  }

  async function handleSend() {
    if (!selectedEnkät || deltagare.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const fn = httpsCallable(functions, 'skickaEnkatUtskick');
      const result = await fn({ workshopId, enkätId: selectedEnkät.id });
      setSendResult({ ok: true, sent: result.data.sent });
      await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { status: 'skickad', skickatAt: serverTimestamp() });
      setSelectedEnkät(prev => ({ ...prev, status: 'skickad' }));
    } catch (e) {
      setSendResult({ ok: false, error: e.message });
    }
    setSending(false);
  }

  function copyLink(d) {
    const url = `${window.location.origin}/enkat/${workshopId}/${selectedEnkät.id}/${d.id}/${d.token}`;
    navigator.clipboard.writeText(url);
    setCopied(d.id);
    setTimeout(() => setCopied(null), 2000);
  }

  const svarsRate = selectedEnkät ? Math.round(
    (deltagare.filter(d => d.harSvarat).length /
      Math.max(1, deltagare.length)) * 100
  ) : 0;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2, position: 'sticky', top: 0, zIndex: 100,
      }}>
        <WorkshopNLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(`/workshopn`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{workshop?.title}</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>Enkäthantering</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, fontWeight: 700 }}>
          Ny enkät
        </Button>
      </Box>

      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 56px)' }}>
        {/* Left: enkät list */}
        <Box sx={{
          width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider',
          p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
          {enkäter.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PollIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="caption" color="text.secondary">Inga enkäter ännu</Typography>
            </Box>
          ) : enkäter.map(e => {
            const answeredCount = e.svaratAntal || 0;
            const totalCount = e.deltagareAntal || 0;
            return (
              <Paper key={e.id} elevation={0} onClick={() => setSelectedEnkät(e)}
                sx={{
                  p: 1.5, borderRadius: 1.5, cursor: 'pointer', border: '1px solid',
                  borderColor: selectedEnkät?.id === e.id ? accent : 'divider',
                  bgcolor: selectedEnkät?.id === e.id ? accent + '10' : 'background.paper',
                  '&:hover': { borderColor: accent },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Chip label={e.typ === '1vecka' ? '1 vecka' : '24h'} size="small"
                      sx={{ bgcolor: accent + '22', color: accent, fontWeight: 700, fontSize: '0.65rem', mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {answeredCount}/{totalCount} svar
                    </Typography>
                    <Chip label={e.status === 'skickad' ? 'Skickad' : 'Utkast'} size="small"
                      sx={{
                        height: 16, fontSize: '0.6rem', mt: 0.5,
                        bgcolor: e.status === 'skickad' ? '#dcfce7' : '#f3f4f6',
                        color: e.status === 'skickad' ? '#16a34a' : '#555',
                      }} />
                  </Box>
                  {e.status === 'skickad' && totalCount > 0 && (
                    <ResponseRing answered={answeredCount} total={totalCount} size={44} strokeWidth={4} />
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>

        {/* Right: management */}
        <Box sx={{ flex: 1, p: 3, minWidth: 0 }}>
          {!selectedEnkät ? (
            <Box sx={{ textAlign: 'center', pt: 8 }}>
              <PollIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">Skapa en enkät för att komma igång</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Enkät – {selectedEnkät.typ === '1vecka' ? '1 vecka efter' : '24–48h efter'} workshop
                  </Typography>
                  <Chip label={selectedEnkät.status === 'skickad' ? 'Skickad' : 'Utkast'}
                    size="small"
                    sx={{
                      bgcolor: selectedEnkät.status === 'skickad' ? '#dcfce7' : '#f3f4f6',
                      color: selectedEnkät.status === 'skickad' ? '#16a34a' : '#555',
                      fontWeight: 700, mt: 0.5,
                    }} />
                </Box>
                {selectedEnkät.status === 'skickad' && deltagare.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <ResponseRing
                      answered={deltagare.filter(d => d.harSvarat).length}
                      total={deltagare.length}
                      size={72}
                      strokeWidth={5}
                    />
                    <Typography fontSize="0.68rem" color="text.secondary">
                      {deltagare.filter(d => d.harSvarat).length}/{deltagare.length} svarat
                    </Typography>
                  </Box>
                )}
              </Box>

              <Tabs value={tab} onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: accent }, '& .MuiTabs-indicator': { bgcolor: accent } }}>
                <Tab label={`Deltagare (${deltagare.length})`} />
                <Tab label={`Frågor (${(selectedEnkät.frågorInhåll || []).filter(f => f.inkludera !== false).length})`} />
                <Tab label="Skicka" />
                <Tab label={`Svar (${svar.length})`} />
              </Tabs>

              {/* Tab 0: Deltagare */}
              {tab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Button variant="outlined" size="small" startIcon={<PersonAddIcon />}
                      onClick={openAddDialog}
                      sx={{ borderColor: accent, color: accent }}>
                      Lägg till deltagare
                    </Button>
                    {(() => {
                      const existingEmails = new Set(deltagare.map(d => d.email.toLowerCase()));
                      const newCount = qrRegistrations.filter(r => r.email && !existingEmails.has(r.email.toLowerCase())).length;
                      return newCount > 0 ? (
                        <Button variant="outlined" size="small" startIcon={<QrCodeScannerIcon />}
                          onClick={handleImportQRRegistrations}
                          disabled={importingQR}
                          sx={{ borderColor: '#4ade80', color: '#4ade80', '&:hover': { bgcolor: '#4ade8011' } }}>
                          {importingQR ? 'Importerar...' : `Importera QR-anmälda (${newCount})`}
                        </Button>
                      ) : null;
                    })()}
                  </Box>

                  {deltagare.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                      <Typography color="text.secondary">Inga deltagare ännu. Lägg till manuellt eller importera.</Typography>
                    </Paper>
                  ) : (
                    <List disablePadding>
                      {deltagare.map(d => (
                        <Paper key={d.id} variant="outlined" sx={{ mb: 1, borderRadius: 1.5 }}>
                          <ListItem
                            secondaryAction={
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title={copied === d.id ? 'Kopierat!' : 'Kopiera enkätlänk'}>
                                  <IconButton size="small" onClick={() => copyLink(d)}>
                                    <ContentCopyIcon sx={{ fontSize: 16, color: copied === d.id ? accent : 'text.secondary' }} />
                                  </IconButton>
                                </Tooltip>
                                {!d.harSvarat && (
                                  <IconButton size="small" onClick={() => handleRemoveDeltagare(d.id)}>
                                    <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                  </IconButton>
                                )}
                              </Box>
                            }>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {d.namn}
                                  {d.harSvarat && (
                                    <Chip icon={<CheckCircleIcon sx={{ fontSize: '12px !important' }} />}
                                      label="Svarat" size="small"
                                      sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, height: 18, fontSize: '0.62rem' }} />
                                  )}
                                </Box>
                              }
                              secondary={d.email}
                            />
                          </ListItem>
                        </Paper>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Tab 1: Frågor */}
              {tab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                    <Button variant="outlined" size="small" startIcon={<AutoAwesomeIcon />}
                      onClick={handleGenerateQuestions}
                      disabled={generatingQuestions}
                      sx={{ borderColor: accent, color: accent }}>
                      {generatingQuestions ? 'Genererar...' : 'Generera AI-frågor'}
                    </Button>
                    {generatingQuestions && <CircularProgress size={16} sx={{ color: accent }} />}
                  </Box>
                  {questionsError && <Alert severity="error" sx={{ mb: 2 }}>{questionsError}</Alert>}

                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                    Generiska frågor (alltid med)
                  </Typography>
                  {(selectedEnkät.frågorInhåll || []).filter(f => f.id.startsWith('g')).map((f, i) => (
                    <Paper key={f.id} variant="outlined" sx={{ mb: 1, p: 1.5, borderRadius: 1.5, opacity: f.inkludera === false ? 0.5 : 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#666' }}>{i + 1}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ flex: 1 }}>{f.fråga}</Typography>
                        <Chip label={f.typ} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#f3f4f6', color: '#888' }} />
                      </Box>
                    </Paper>
                  ))}

                  {(selectedEnkät.frågorInhåll || []).filter(f => f.id.startsWith('ai')).length > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1, mt: 2 }}>
                        AI-genererade frågor
                      </Typography>
                      {(selectedEnkät.frågorInhåll || []).filter(f => f.id.startsWith('ai')).map((f, i) => (
                        <Paper key={f.id} variant="outlined"
                          sx={{ mb: 1, p: 1.5, borderRadius: 1.5, borderColor: f.inkludera === false ? 'divider' : accent + '44', opacity: f.inkludera === false ? 0.5 : 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: accent, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ flex: 1 }}>{f.fråga}</Typography>
                            <FormControlLabel
                              control={
                                <Switch size="small" checked={f.inkludera !== false}
                                  onChange={e => toggleFråga(f.id, e.target.checked)}
                                  sx={{ '& .Mui-checked': { color: accent }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: accent } }}
                                />
                              }
                              label="" sx={{ m: 0 }} />
                          </Box>
                        </Paper>
                      ))}
                    </>
                  )}
                </Box>
              )}

              {/* Tab 2: Skicka */}
              {tab === 2 && (
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  {/* Left: form */}
                  <Box sx={{ flex: '0 0 460px', minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={700}>Workshopsammanfattning (visas för deltagarna)</Typography>
                        <Tooltip title={workshop?.aiAnalysis ? 'Importera från AI-analysen – välj vilka block du vill ha med' : 'Ingen AI-analys finns för denna workshop ännu'}>
                          <span>
                            <Button size="small" startIcon={<DownloadingIcon />}
                              disabled={!workshop?.aiAnalysis}
                              onClick={() => {
                                setAiSelectedBlocks(new Set(AI_BLOCKS.map(b => b.key)));
                                setAiImportDialog(true);
                              }}
                              sx={{ flexShrink: 0, color: accent, borderColor: accent, textTransform: 'none', fontSize: '0.75rem' }}
                              variant="outlined">
                              Importera från AI-analys
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Klistra in eller skriv en kort sammanfattning av workshopens resultat och åtgärdsförslag. Deltagarna ser detta överst i enkäten som påminnelse.
                      </Typography>
                      <TextField
                        fullWidth multiline rows={5}
                        placeholder="T.ex. de viktigaste slutsatserna, rankade åtgärder, nästa steg..."
                        value={selectedEnkät.sammanfattning || ''}
                        onChange={async e => {
                          const val = e.target.value;
                          setSelectedEnkät(prev => ({ ...prev, sammanfattning: val }));
                          await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { sammanfattning: val });
                        }}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: accent } }}
                      />
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>Nästa steg (valfritt)</Typography>
                        <FormControlLabel
                          control={
                            <Switch size="small" checked={selectedEnkät.nästaSteg?.aktiv || false}
                              onChange={async e => {
                                const current = selectedEnkät.nästaSteg || {};
                                const defaultText = !current.text?.trim() && e.target.checked
                                  ? 'Vi genomför nu en SWOT-analys baserat på workshopens resultat. SWOT (Styrkor, Svagheter, Möjligheter, Hot) hjälper oss att strukturera insikterna och ta fram en konkret handlingsplan.'
                                  : current.text;
                                const ns = { ...current, aktiv: e.target.checked, text: defaultText ?? '' };
                                setSelectedEnkät(prev => ({ ...prev, nästaSteg: ns }));
                                await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { nästaSteg: ns });
                              }}
                              sx={{ '& .Mui-checked': { color: accent }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: accent } }}
                            />
                          }
                          label="" sx={{ m: 0 }} />
                      </Box>
                      {selectedEnkät.nästaSteg?.aktiv && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Deltagarna ser ett nästa-steg-kort i enkäten. T.ex. info om kommande SWOT-analys.
                          </Typography>
                          <TextField fullWidth multiline rows={3}
                            label="Beskriv nästa steg"
                            placeholder="T.ex. Vi kör nu en SWOT-analys baserat på workshopens resultat. SWOT hjälper oss att..."
                            value={selectedEnkät.nästaSteg?.text || ''}
                            onChange={async e => {
                              const ns = { ...(selectedEnkät.nästaSteg || {}), text: e.target.value };
                              setSelectedEnkät(prev => ({ ...prev, nästaSteg: ns }));
                              await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { nästaSteg: ns });
                            }}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: accent } }}
                          />
                          <TextField fullWidth
                            label="När sker nästa steg? (datum eller fritext)"
                            placeholder="T.ex. 2026-05-15 eller Om två BTM-möten"
                            value={selectedEnkät.nästaSteg?.datum || ''}
                            onChange={async e => {
                              const ns = { ...(selectedEnkät.nästaSteg || {}), datum: e.target.value };
                              setSelectedEnkät(prev => ({ ...prev, nästaSteg: ns }));
                              await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { nästaSteg: ns });
                            }}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: accent } }}
                          />
                        </Box>
                      )}
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Utskick</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField fullWidth size="small" label="Ämnesrad i mailet"
                          value={selectedEnkät.mailSubject ?? `Workshopenkät: ${selectedEnkät.workshopTitle || ''}${selectedEnkät.workshopDate ? ' – ' + selectedEnkät.workshopDate : ''}`}
                          onChange={async e => {
                            setSelectedEnkät(prev => ({ ...prev, mailSubject: e.target.value }));
                            await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { mailSubject: e.target.value });
                          }}
                          sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: accent } }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {deltagare.length} deltagare · {(selectedEnkät.frågorInhåll || []).filter(f => f.inkludera !== false).length} frågor (Del 1) + 5 om verktyget (Del 2)
                        </Typography>
                      </Box>
                    </Paper>

                    {sendResult?.ok && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {sendResult.sent} mail skickade!
                      </Alert>
                    )}
                    {sendResult?.ok === false && (
                      <Alert severity="error" sx={{ mb: 2 }}>{sendResult.error}</Alert>
                    )}

                    {(() => {
                      const all = deltagare;
                      const unsent = all.filter(d => !d.skickatAt);
                      const alreadySent = all.filter(d => d.skickatAt);
                      return (
                        <>
                          {alreadySent.length > 0 && (
                            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                              {alreadySent.length === all.length
                                ? `Alla ${all.length} deltagare har redan fått mail. Nytt utskick skickar till alla igen.`
                                : `${alreadySent.length} av ${all.length} har redan fått mail — de hoppas över. ${unsent.length} får nytt mail.`}
                            </Alert>
                          )}
                          <Button variant="contained" size="large" startIcon={<SendIcon />}
                            onClick={handleSend}
                            disabled={sending || all.length === 0}
                            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, fontWeight: 700, '&.Mui-disabled': { bgcolor: '#ddd', color: '#999' } }}>
                            {sending ? 'Skickar...' : unsent.length === 0 ? 'Skicka enkät (alla redan fått)' : `Skicka till ${unsent.length} mottagare`}
                          </Button>
                        </>
                      );
                    })()}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Varje deltagare får en unik länk. Länkarna går att kopiera manuellt under fliken Deltagare om du vill skicka dem själv.
                    </Typography>
                  </Box>

                  {/* Right: email preview */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                      Förhandsvisning – så ser mailet ut
                    </Typography>
                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 480, bgcolor: '#f5f5f5' }}>
                      {/* Mail meta */}
                      <Box sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #e8e8e8', px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                          <strong>Från:</strong> LagbasN &lt;noreply@lagbasn.se&gt;
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                          <strong>Till:</strong> [Deltagare]
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#555', fontWeight: 600 }}>
                          <strong>Ämne:</strong> {selectedEnkät.mailSubject || `Workshopenkät: ${selectedEnkät.workshopTitle || ''}${selectedEnkät.workshopDate ? ' – ' + selectedEnkät.workshopDate : ''}`}
                        </Typography>
                      </Box>
                      {/* Mail body */}
                      <Box sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                        <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                          {/* Header */}
                          <Box sx={{ bgcolor: '#1a1a1a', px: 3, py: 2 }}>
                            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#fff' }}>
                              Workshop<span style={{ color: accent }}>N</span>
                            </Typography>
                          </Box>
                          {/* Hero image */}
                          {selectedEnkät.workshopImageUrl && (
                            <Box sx={{ position: 'relative', height: 120, overflow: 'hidden' }}>
                              <Box component="img" src={selectedEnkät.workshopImageUrl} alt=""
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              <Box sx={{
                                position: 'absolute', inset: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 100%)',
                                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', px: 2, py: 1.5,
                              }}>
                                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                                  {selectedEnkät.workshopTitle || 'Workshop'}
                                </Typography>
                                {selectedEnkät.workshopDate && (
                                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem' }}>
                                    {selectedEnkät.workshopDate}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          {/* Body */}
                          <Box sx={{ px: 3, py: 2.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a', mb: 0.75 }}>
                              Hej [Deltagare]!
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.7, mb: 2 }}>
                              Det har gått {selectedEnkät.typ === '1vecka' ? '1 vecka' : '24–48h'} sedan workshopen{' '}
                              <strong>{selectedEnkät.workshopTitle || ''}</strong>.
                              Vi vill gärna höra vad du tyckte – det tar bara 3–4 minuter.
                            </Typography>
                            {/* Summary preview */}
                            {selectedEnkät.sammanfattning?.trim() && (
                              <Box sx={{ bgcolor: '#f8f8f8', border: '1px solid #e8e8e8', borderRadius: 1, p: 1.5, mb: 2 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                  Workshopens resultat – en påminnelse
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#444', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                  {selectedEnkät.sammanfattning.length > 200
                                    ? selectedEnkät.sammanfattning.slice(0, 200) + '…'
                                    : selectedEnkät.sammanfattning}
                                </Typography>
                              </Box>
                            )}
                            {/* Nästa steg preview */}
                            {selectedEnkät.nästaSteg?.aktiv && (
                              <Box sx={{ bgcolor: '#1a1a1a', borderRadius: 1, p: 1.5, mb: 2 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: accent, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                  Nästa steg
                                </Typography>
                                {selectedEnkät.nästaSteg.text && (
                                  <Typography sx={{ fontSize: '0.78rem', color: '#ccc', lineHeight: 1.5, mb: 0.5 }}>
                                    {selectedEnkät.nästaSteg.text.length > 150
                                      ? selectedEnkät.nästaSteg.text.slice(0, 150) + '…'
                                      : selectedEnkät.nästaSteg.text}
                                  </Typography>
                                )}
                                {selectedEnkät.nästaSteg.datum && (
                                  <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>
                                    När: {selectedEnkät.nästaSteg.datum}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {/* CTA */}
                            <Box sx={{ bgcolor: accent, borderRadius: 1, py: 1.25, px: 2, textAlign: 'center', mb: 1 }}>
                              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>
                                Svara på enkäten →
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.68rem', color: '#bbb', mt: 1 }}>
                              Länken är personlig och kan bara användas en gång.
                            </Typography>
                          </Box>
                          {/* Footer */}
                          <Box sx={{ borderTop: '1px solid #f0f0f0', px: 3, py: 1.5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.68rem', color: '#bbb' }}>LagbasN · WorkshopN</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              )}

              {/* Tab 3: Svar */}
              {tab === 3 && (
                <Box>
                  {selectedEnkät?.skapadAv !== currentUser?.uid && workshop?.createdBy !== currentUser?.uid && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Du har inte behörighet att se svaren. Endast workshopägaren och AO-ansvarig kan se enkätsvaren.
                    </Alert>
                  )}
                  {(selectedEnkät?.skapadAv === currentUser?.uid || workshop?.createdBy === currentUser?.uid) && svar.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" variant="outlined" onClick={() => window.print()}
                        sx={{ borderColor: 'divider', color: 'text.secondary', textTransform: 'none' }}>
                        🖨 Skriv ut rapport
                      </Button>
                    </Box>
                  )}
                  {(selectedEnkät?.skapadAv !== currentUser?.uid && workshop?.createdBy !== currentUser?.uid) ? null : svar.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                      <Typography color="text.secondary">Inga svar ännu.</Typography>
                    </Paper>
                  ) : (
                    <>
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{svar.length} svar</Typography>
                        <LinearProgress variant="determinate" value={svarsRate}
                          sx={{ flex: 1, borderRadius: 1, bgcolor: '#eee', '& .MuiLinearProgress-bar': { bgcolor: accent } }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>{svarsRate}%</Typography>
                      </Box>

                      {(selectedEnkät.frågorInhåll || []).filter(f => f.inkludera !== false).map((f, qi) => {
                        const svarPåFråga = svar.map(s => s.svar?.find(sv => sv.frågaId === f.id)?.svar).filter(Boolean);
                        return (
                          <Paper key={f.id} variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight={700} gutterBottom>{qi + 1}. {f.fråga}</Typography>
                            {f.typ === 'skala' && (
                              <Box>
                                {[1, 2, 3, 4, 5].map(n => {
                                  const count = svarPåFråga.filter(v => Number(v) === n).length;
                                  const pct = Math.round(count / Math.max(1, svarPåFråga.length) * 100);
                                  return (
                                    <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ width: 16 }}>{n}</Typography>
                                      <Box sx={{ flex: 1, height: 12, bgcolor: '#f3f4f6', borderRadius: 1, overflow: 'hidden' }}>
                                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: accent, borderRadius: 1 }} />
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">{count}</Typography>
                                    </Box>
                                  );
                                })}
                                <Typography variant="caption" color="text.secondary">
                                  Snitt: {svarPåFråga.length > 0 ? (svarPåFråga.reduce((a, b) => a + Number(b), 0) / svarPåFråga.length).toFixed(1) : '–'}
                                </Typography>
                              </Box>
                            )}
                            {(f.typ === 'text' || f.typ === 'janej') && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {svarPåFråga.map((v, i) => (
                                  <Box key={i} sx={{ bgcolor: accent + '0A', borderLeft: `3px solid ${accent}`, p: 1, borderRadius: '0 6px 6px 0' }}>
                                    <Typography variant="body2">{v}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}

                      {appfeedback.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Appfeedback (anonym, {appfeedback.length} svar)
                          </Typography>
                          {['v1', 'v4'].map(vid => {
                            const frågetext = vid === 'v1' ? 'Hur upplevde du WorkshopN som verktyg?' : 'Skulle du rekommendera detta upplägg?';
                            const svarList = appfeedback.map(a => a.svar?.find(sv => sv.frågaId === vid)?.svar).filter(Boolean);
                            if (svarList.length === 0) return null;
                            return (
                              <Paper key={vid} variant="outlined" sx={{ mb: 1.5, p: 2, borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight={700} gutterBottom>{frågetext}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Snitt: {(svarList.reduce((a, b) => a + Number(b), 0) / svarList.length).toFixed(1)} / 5
                                </Typography>
                              </Paper>
                            );
                          })}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Add participant dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700} sx={{ pb: 0 }}>Lägg till deltagare</DialogTitle>
        <Tabs value={addTab} onChange={(_, v) => { setAddTab(v); setSelectedToAdd(new Set()); }}
          sx={{ px: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }, '& .Mui-selected': { color: accent }, '& .MuiTabs-indicator': { bgcolor: accent } }}>
          <Tab label="Manuellt" />
          <Tab label={`Workshopdeltagare (${workshopResponders.length})`} />
          <Tab label="Registrerade användare" />
        </Tabs>
        <Divider />
        <DialogContent sx={{ pt: 2, minHeight: 220 }}>
          {addTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Namn *" fullWidth autoFocus value={newNamn} onChange={e => setNewNamn(e.target.value)} />
              <TextField label="E-post *" type="email" fullWidth value={newEmail} onChange={e => setNewEmail(e.target.value)} />
              <Button variant="contained" onClick={async () => { await handleAddDeltagare(); }}
                disabled={!newNamn.trim() || !newEmail.trim()}
                sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, alignSelf: 'flex-start' }}>
                Lägg till
              </Button>
              {deltagare.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                    Redan tillagda ({deltagare.length})
                  </Typography>
                  {deltagare.map(d => (
                    <Chip key={d.id} label={`${d.namn} · ${d.email}`} size="small"
                      onDelete={() => handleRemoveDeltagare(d.id)}
                      sx={{ m: 0.25, fontSize: '0.7rem' }} />
                  ))}
                </Box>
              )}
            </Box>
          )}
          {(addTab === 1 || addTab === 2) && (
            <Box>
              {loadingPeople ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                  <CircularProgress size={24} sx={{ color: accent }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField size="small" placeholder="Sök namn eller e-post..." sx={{ flex: 1 }}
                      value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                    {addTab === 2 && (
                      <TextField select size="small" sx={{ width: 160 }}
                        value={userFilterAO} onChange={e => setUserFilterAO(e.target.value)}>
                        <option value="">Alla AO</option>
                        {[...new Set(allUsers.map(u => u.departmentName).filter(Boolean))].sort().map(ao => (
                          <option key={ao} value={ao}>{ao}</option>
                        ))}
                      </TextField>
                    )}
                  </Box>
                  {(() => {
                    const source = addTab === 1 ? workshopResponders : allUsers;
                    const filtered = source.filter(u => {
                      const q = userSearch.toLowerCase();
                      const matchSearch = !q || (u.namn || u.displayName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      const matchAO = !userFilterAO || u.departmentName === userFilterAO;
                      return matchSearch && matchAO;
                    });
                    if (filtered.length === 0) return (
                      <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                        {addTab === 1 ? 'Inga workshopdeltagare med LagbasN-konton hittades' : 'Inga användare hittades'}
                      </Typography>
                    );
                    return (
                      <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {filtered.map(u => {
                          const key = u.id || u.email;
                          const checked = selectedToAdd.has(key);
                          return (
                            <Box key={key} onClick={() => setSelectedToAdd(prev => {
                              const next = new Set(prev);
                              checked ? next.delete(key) : next.add(key);
                              return next;
                            })}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1.5,
                                cursor: 'pointer', userSelect: 'none',
                                bgcolor: checked ? accent + '10' : 'transparent',
                                '&:hover': { bgcolor: checked ? accent + '18' : 'action.hover' },
                              }}>
                              <Box sx={{
                                width: 18, height: 18, borderRadius: 0.5, border: '2px solid',
                                borderColor: checked ? accent : '#bbb', bgcolor: checked ? accent : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>
                                {checked && <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 900, lineHeight: 1 }}>✓</Typography>}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{u.namn || u.displayName || u.email}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{u.email}{u.departmentName ? ` · ${u.departmentName}` : ''}</Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialog(false)}>Stäng</Button>
          {(addTab === 1 || addTab === 2) && (
            <Button variant="contained" onClick={handleAddSelected}
              disabled={selectedToAdd.size === 0}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' } }}>
              Lägg till {selectedToAdd.size > 0 ? `(${selectedToAdd.size})` : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* AI import dialog */}
      <Dialog open={aiImportDialog} onClose={() => setAiImportDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700} sx={{ pb: 0 }}>Importera från AI-analys</DialogTitle>
        <Divider sx={{ mt: 1.5 }} />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bocka av de block du <strong>inte</strong> vill ha med. Texten går att redigera fritt efteråt.
          </Typography>
          {AI_BLOCKS.map(b => {
            const content = workshop?.aiAnalysis ? b.get(workshop.aiAnalysis) : '';
            if (!content) return null;
            const checked = aiSelectedBlocks.has(b.key);
            return (
              <Paper key={b.key} variant="outlined" onClick={() => setAiSelectedBlocks(prev => {
                const next = new Set(prev);
                checked ? next.delete(b.key) : next.add(b.key);
                return next;
              })}
                sx={{
                  mb: 1.5, p: 1.5, borderRadius: 1.5, cursor: 'pointer',
                  borderColor: checked ? accent : 'divider',
                  bgcolor: checked ? accent + '08' : 'background.paper',
                  opacity: checked ? 1 : 0.5,
                  transition: 'all .15s',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Checkbox size="small" checked={checked} sx={{ p: 0, mt: 0.2, color: accent, '&.Mui-checked': { color: accent } }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {b.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', fontSize: '0.78rem' }}>
                      {content}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAiImportDialog(false)}>Avbryt</Button>
          <Button variant="contained" disabled={aiSelectedBlocks.size === 0}
            onClick={async () => {
              const text = buildSammanfattningFromAI(workshop.aiAnalysis, aiSelectedBlocks);
              setSelectedEnkät(prev => ({ ...prev, sammanfattning: text }));
              await updateDoc(doc(db, 'workshops', workshopId, 'enkäter', selectedEnkät.id), { sammanfattning: text });
              setAiImportDialog(false);
            }}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' } }}>
            Importera markerade block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create enkät dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Ny enkät</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Välj när enkäten skickas relativt workshopen:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
            {[
              { val: '24h', label: '24–48h efter', desc: 'Känsla och första reflektion' },
              { val: '1vecka', label: '1 vecka efter', desc: 'Uppföljning – har något hänt?' },
            ].map(o => (
              <Paper key={o.val} variant="outlined" onClick={() => setNewTyp(o.val)}
                sx={{
                  p: 2, borderRadius: 2, cursor: 'pointer',
                  borderColor: newTyp === o.val ? accent : 'divider',
                  bgcolor: newTyp === o.val ? accent + '0A' : 'background.paper',
                }}>
                <Typography variant="body2" fontWeight={700}>{o.label}</Typography>
                <Typography variant="caption" color="text.secondary">{o.desc}</Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateEnkät} disabled={creating}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' } }}>
            {creating ? 'Skapar...' : 'Skapa enkät'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
